import { useEffect, useState, useRef } from "react";
import { useOutletContext } from 'react-router-dom';
import Workspace from "../components/Workspace";
import { Accordion, ThreeDotsLoading } from "../components/primitives/";
import workspaceStyles from "../components/Workspace.module.css";
import { IoIosSearch } from "react-icons/io";
import AnimatedList from "../components/AnimatedList";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from '../hooks/useTranslation';
import { useSearchParams } from 'react-router-dom';

export default function Dashboard() {
    const t = useTranslation();

    const { clientData, clientDataLoading, error, setIsWorkspaceRegisterOpen } = useOutletContext();
    const [workspaceList, setWorkspaceList] = useState(null);
    const [filteredWorkspaceList, setFilteredWorkspaceList] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [searchParams] = useSearchParams();

    useEffect(() => {
        
        if (clientDataLoading || !clientData || !clientData.workspaces)
            return;
        if (Object.keys(clientData.workspaces).length === 0) {
            setIsWorkspaceRegisterOpen(true);
        }
        setWorkspaceList(clientData.workspaces);
        const filtered = {};
        Object.entries(clientData.workspaces).forEach(([workspaceId, workspace]) => {
            if (!searchValue || (searchValue && workspace.workspaceName && workspace.workspaceName.toLowerCase().includes(searchValue.toLowerCase()))) {
                filtered[workspaceId] = workspace;
            }
        });
        
        // Convert filtered object to array of key-value pairs
        const sortable = Object.entries(filtered).map(([workspaceId, workspace]) => ({
            [workspaceId]: workspace
        }));
        
        const sortedWorkspaces = sortable.sort((a, b) => {
            const objectA = Object.values(a)[0];
            const objectB = Object.values(b)[0];

            if (!searchValue) {
                // First try to sort by listOrder
                if (objectA.listOrder && objectB.listOrder && objectA.listOrder !== objectB.listOrder) {
                    return objectA.listOrder - objectB.listOrder;
                }
                // Fall back to createdAt if listOrder not available
                if (objectA.createdAt && objectB.createdAt) {
                    return objectA.createdAt.toDate() - objectB.createdAt.toDate();
                }
            }
            return 0;
        });

        // Debugging single-item design
        const singleWorkspace = sortedWorkspaces.slice(0, 1);

        setFilteredWorkspaceList(sortedWorkspaces);
        //setWorkspaceList(singleWorkspace);
    }, [clientData, searchValue]);

    function handleSearchChange(e) {
        setSearchValue(e.target.value);
    }

    return (
        <div className={workspaceStyles.list}>
            {error && <span className="text-danger">{error?.message || "Failed to load client data."}</span>}
            {clientDataLoading && <ThreeDotsLoading />}
            {!clientDataLoading && workspaceList && Object.keys(workspaceList).length === 0 ? (
                <div style={{marginTop: "50px", textAlign: "center", fontSize: "40px", color: "#6c6c6c", fontWeight: "bold"}}>
                    {t('You don\'t have any workspaces yet')}
                </div>
            ) : (
                <>
                    <AnimatePresence mode="wait">
                        {workspaceList && Object.keys(workspaceList).length > 1 && (
                            <motion.div style={{width: "100%"}}
                                layout
                                initial={{ y: -20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: -20, opacity: 0 }}
                                transition={{ 
                                    duration: 0.3
                                }}
                            >
                                <div className={workspaceStyles.searchbar}>
                                    <form className={workspaceStyles.searchbarForm}>
                                        <IoIosSearch className={workspaceStyles.searchIcon} />
                                        <input 
                                            className={workspaceStyles.searchbarInput} 
                                            placeholder={t('Search')} 
                                            value={searchValue} 
                                            onChange={handleSearchChange} 
                                            type="text" 
                                        />
                                    </form>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    {filteredWorkspaceList && (
                        <Accordion>
                            <AnimatedList
                                items={filteredWorkspaceList}
                                keyExtractor={workspaceObj => Object.keys(workspaceObj)[0]}
                                renderItem={workspaceObj => {
                                    const [[workspaceId, workspace]] = Object.entries(workspaceObj);
                                    return (
                                        <Workspace 
                                            workspaceId={workspaceId} 
                                            workspace={workspace} 
                                            clientData={clientData}
                                            forcedOpen={Object.keys(workspaceList).length === 1 || searchParams.get('workspace') === workspaceId}
                                        />
                                    );
                                }}
                            />
                        </Accordion>
                    )}
                    {filteredWorkspaceList?.length === 0 && (
                        <div style={{marginTop: "50px", textAlign: "center", fontSize: "40px", color: "#6c6c6c", fontWeight: "bold"}}>
                            {t('No workspaces match your search')}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}